import React, { useMemo } from 'react';
import { METRICS, getDeviation, deviationColor } from './individualTargetsShared';

// Einstein compose panel for Individual Targets (2026-09-10) — same pattern as the other panels,
// but the underlying math is genuinely per-(player, week): target resolution is an async batch
// call (resolveTargetsBatch), not a pure client-side reduction over one shared team-wide fetch
// like MatchChronicPanel/MedicalPanel. EinsteinComposePanels.js's PANEL_REGISTRY entry for this
// key batches every panel's (player_id, iso_week) into ONE resolveTargetsBatch call up front
// (fetchIndividualTargetsSummaries in individualTargetsShared.js) and hands each panel its own
// pre-computed summary — this component itself does no fetching, purely presentational like the
// others. Shows 3 of the full page's 7 metrics (TD/HID/SA — one from each of its Endurance/High
// velocity/Small spaces categories) rather than the full gauge grid, to stay compact.
const PANEL_METRIC_KEYS = ['td', 'hid', 'sa'];
const f0 = n => (n == null || isNaN(n) ? '—' : Math.round(n).toLocaleString());

export default function IndividualTargetsPanel({ playerId, iso_week, players, summariesByRequest }) {
  const player = useMemo(() => players.find(p => String(p.id) === String(playerId)), [players, playerId]);
  const summary = summariesByRequest?.get(`${playerId}|${iso_week}`);

  if (!player) {
    return <div style={{ padding: 16, color: 'var(--text-dimmed)', fontSize: 13 }}>No player found for id {playerId}.</div>;
  }

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '18px 20px', minWidth: 260 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 2 }}>{player.name}</div>
      <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginBottom: 14 }}>
        {iso_week ? iso_week.replace('-W', ' · Week ') : 'Individual targets'}
      </div>
      {!summary ? (
        <div style={{ color: 'var(--text-dimmed)', fontSize: 12 }}>No sessions found for that week.</div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
            {PANEL_METRIC_KEYS.map(key => {
              const m = METRICS.find(x => x.key === key);
              const actual = summary.actual[key];
              const t = summary.targets[key];
              const dev = t ? getDeviation(actual, t.lower, t.upper) : null;
              return (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 999, background: deviationColor(dev), flex: 'none' }} />
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)', width: 90 }}>{m.label}</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>
                    {f0(actual)}{m.unit}
                    {t && (t.upper > 0) && (
                      <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> / {f0(t.lower)}–{f0(t.upper)}{m.unit}</span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-dimmed)' }}>
            {summary.sessionCount} session{summary.sessionCount !== 1 ? 's' : ''} that week
          </div>
        </>
      )}
    </div>
  );
}
